import { ITeam } from "@teammood/team";
import { TranslateService } from "@ngx-translate/core";
import { Choice, PollType } from "../polls.type";
import { TaskActionContentDTO } from "../../taskbook-triage/taskbook.types";

export interface CreatePollDialogData {
    teams: ITeam[],
    author: string,
    type: 'ScaleQuestion'
    pollTemplates: PollTemplate[]
}

export interface PollPostNowConfirmationDialogData {
    teams: ITeam[],
    author: string,
    type: 'ScaleQuestion'
    pollTemplates: PollTemplate[]
}

export class PollUnderCreation implements TaskActionContentDTO {
    id: string = 'questionCreation';
    label: string;
    answerOptions: AnswerChoices;
    _target: PollTarget;
    _scheduleOptions: ScheduleOptions;
    _displayAuthor: boolean = true;
    _allowComments: boolean = true;
    _serializePoll: boolean = false;

    static fromTemplate(template: PollTemplate): PollUnderCreation {
        const question = new PollUnderCreation();
        question.label = template.label;
        question.answerOptions = PollUnderCreation.fromTemplateAnswerOptionToChoiceList(template.answerOptions);
        question._target = undefined;
        question._scheduleOptions = {
            publicationDate: new Date(),
            displayDuration: 10,
            recurrence: 'once'
        }
        return question;
    }

    static fromEmpty(): PollUnderCreation {
        const question = new PollUnderCreation();
        question.label = '';
        question.answerOptions = {
            type: 'ClosedQuestion',
            choices: [{ label: '' }, { label: '' }]
        };
        question._target = undefined;
        question._scheduleOptions = {
            publicationDate: new Date(),
            displayDuration: 10,
            recurrence: 'once'
        }
        return question;
    }

    set target(target: PollTarget) {
        this._target = target;
    }

    get target(): PollTarget {
        return this._target;
    }

    toggleDisplayAuthor() {
        this._displayAuthor = !this._displayAuthor;
    }

    toggleSerializePoll() {
        this._serializePoll = !this._serializePoll;
    }

    toggleAllowComments() {
        this._allowComments = !this._allowComments;
    }

    set scheduleOptions(options: ScheduleOptions) {
        this._scheduleOptions = options;
    }

    get scheduleOptions(): ScheduleOptions {
        return this._scheduleOptions;
    }

    static fromTemplateAnswerOptionToChoiceList(answerOptions: AnswerOptions): AnswerChoices{
        if(answerOptions.type === 'ScaleQuestion'){
             // model of template ScaleOptions to check
             let scaleOptions = answerOptions as ScaleOptions;
             let choices: ListChoice[];
             if(scaleOptions.negative && scaleOptions.positive){
                 choices = [{label: scaleOptions.negative}, {label: scaleOptions.positive}]
             }else{
                choices = [scaleOptions.choices[0].label, scaleOptions.choices[1].label]
             }
             return {
                 type: 'ScaleQuestion',  
                 choices: choices
             } as AnswerChoices;

        }else{
            return {
                type: answerOptions.type,
                choices: answerOptions.choices.map(choice => {
                    return {label: choice.label}
                })
            };
        }    
    }

    static choiceListFromNewType(answerOptions: AnswerChoices, translateService: TranslateService): ListChoice[]{
       switch(answerOptions.type){
            case 'AgreeDisagreeQuestion':
                return [
                        {label: translateService.instant('poll_creation.agree_disagree_question.strongly_disagree')}, 
                        {label: translateService.instant('poll_creation.agree_disagree_question.disagree')}, 
                        {label: translateService.instant('poll_creation.agree_disagree_question.neutral')}, 
                        {label: translateService.instant('poll_creation.agree_disagree_question.agree')}, 
                        {label: translateService.instant('poll_creation.agree_disagree_question.strongly_agree')}, 
                    ];
            case 'FiveStarsQuestion':
                return [{label: "1"},{label: "2"},{label: "3"},{label: "4"}, {label: "5"}];
            case 'ScaleQuestion':
                const negative = { label: answerOptions.choices[0].label }
                const positive = { label: answerOptions.choices[answerOptions.choices.length-1].label }
                return [negative, {label: "2"},{label: "3"},{label: "4"}, positive];

            case 'ClosedQuestion':
                return answerOptions.choices;
        }
    }

    static fromChoiceListToTypedAnswerOption(answerChoices: AnswerChoices, translateService: TranslateService): Choice[] {
        switch(answerChoices.type){
            case 'AgreeDisagreeQuestion':
                return [
                        {label: translateService.instant('poll_creation.agree_disagree_question.strongly_disagree'), position: 1, id: '1'},
                        {label: translateService.instant('poll_creation.agree_disagree_question.disagree'), position: 2, id: '2'},
                        {label: translateService.instant('poll_creation.agree_disagree_question.neutral'), position: 3, id: '3'},
                        {label: translateService.instant('poll_creation.agree_disagree_question.agree'), position: 4, id: '4'},
                        {label: translateService.instant('poll_creation.agree_disagree_question.strongly_agree'), position: 5, id: '5'}
                    ];
            case 'FiveStarsQuestion':
                return [{id: "1", label: "1", position: 1},{id: "2", label: "2", position: 2},{id: "3", label: "3", position: 3},{id: "4", label: "4", position: 4}, {id: "5", label: "5", position: 5}];
            case 'ScaleQuestion':
                const negative = {
                    label: answerChoices.choices[0].label,
                    id: 'negative',
                    position: 1
                }
                const positive = {
                    label: answerChoices.choices[answerChoices.choices.length-1].label,
                    id: 'positive',
                    position: 5
                }
                return [negative, {id: "2", label: "2", position: 2},{id: "3", label: "3", position: 3},{id: "4", label: "4", position: 4}, positive];
            case 'ClosedQuestion':
                let num = 0;
                return answerChoices.choices.map(choice => {
                    return {
                        label: choice.label,
                        id: choice.label,
                        position: num++
                    }
                })
        }
    }
}


export class PollTemplate {
    id: string; 
    label: string;
    answerOptions: AnswerOptions;
}

export interface ScheduleOptions {
    publicationDate: Date;
    displayDuration: number;
    recurrence: RecurrenceType;
}
export type RecurrenceType = 'once' | 'weekly' | 'monthly';

export interface RecurrencePollResults {
    recurrenceType: 'weekly' | 'monthly';
    dates: Date[];
    values: RecurrencePollResultValue[]
}

export interface RecurrencePollResultValue {
    date: Date;
    value: number
}

export interface ClosedRecurrencePollResults {
    recurrenceType: 'weekly' | 'monthly';
    dates: Date[]
    values: ClosedRecurrencePollResultValue[]
}

export interface ClosedRecurrencePollResultValue {
    teamId: string;
    values: ClosedRecurrencePollResultOccurenceValue[];
}

export interface ClosedRecurrencePollResultOccurenceValue {
    choice: string;
    percentage: number
}

export interface RecurrencePreviewOption{
    type: RecurrenceType;
    value: string
}

export class AnswerOptions {
    type: PollType;
    choices?: any[];
    positive?: string;
    negative?: string;
}

export class AnswerChoices {
    type: PollType;
    choices: ListChoice[];
}
export interface ListChoice{
    label: string
}

export class ClosedOptions extends AnswerOptions{
    override type: PollType = 'ClosedQuestion';
    override choices: ClosedChoice[] = [];

    constructor(choices: ClosedChoice[]) {
        super();
        this.choices = choices;
    }
}

export interface ClosedChoice{
    label: string;
    position?: number;
    id?: string
}

export class ScaleOptions extends AnswerOptions {
    override type: PollType = 'ScaleQuestion';
    override positive: string; 
    override negative: string;

}

export class AgreeDisagreeOptions extends AnswerOptions {
    override type: PollType = 'AgreeDisagreeQuestion';
    override choices: ADChoices[] = [];
}

interface ADChoices {
    label: string; 
    position: number;
    id?: string
}

export class FiveStarsOptions extends AnswerOptions {
    override type: PollType = 'FiveStarsQuestion';
}

export interface PollTarget {
}

export class TeamPollTarget implements PollTarget {
    teamIds: string[];
}

export class QuestionForPreview {
    label: string;
    choices: Choice[];
}

export class QuestionTypeKey{
    type: PollType;
    translateKey: string;
}